import useNotifications from '@/composables/useNotifications'
import { useRouter } from '@core/utils/utils'
// import useJwt from '@/auth/jwt/useJwt'
import { ref } from '@vue/composition-api'
import i18n from '@/libs/i18n'
import { useLambda } from '@/views/habit/awsLambda'
// import { defaultPassword } from '@/constants'
import { oid } from '@/helpers/mongoHelpers'
import store from '@/store'
import { hasPermission } from '@/composables/usePermission'
import useAuditLog from '@/composables/useAuditLog'

export default function useWorkerEdit() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { route, router } = useRouter()
  const { updateItem, getItem, updateCognitoEmail } = useLambda()
  const { addAuditEntry } = useAuditLog()
  const userData = store.state?.userStore?.userData
  const worker = ref({})
  const isSubmitting = ref(false)
  const workerObjectId = oid(route.value.params.id)
  // Bug de seguridad corregido: sin client_id, cualquier usuario autenticado de cualquier
  // cliente podía ver/editar (incluido escalar privileges) el worker de otro cliente
  // conociendo el _id — hallazgo más serio del inventario original, documentado en
  // permisos-plan.md, deliberadamente sin corregir hasta que le tocara el turno.
  const query = { _id: workerObjectId, client_id: oid(userData.client.$oid) }
  const collection = 'worker'
  const originalEmail = ref('')
  const originalData = ref(null)

  async function show() {
    isSubmitting.value = true

    try {
      const item = await getItem({ collection, query })
      if (!item) throw new Error('Item not found')

      // Inicializar history si no existe (documentos legacy)
      if (!item.history) {
        item.history = []
      }

      worker.value = {
        ...item,
        realEmail: item.realEmail || '',
        supervisors: item.supervisors?.map(e => e.toString()) || [],
        locations: item.locations?.map(e => e.toString()) || [],
        roles: item.roles?.map(e => e.toString()) || [],
        company: item.company?.map(e => e.toString()) || [],
      }
      originalEmail.value = item.email || ''
      originalData.value = JSON.parse(JSON.stringify(item))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.worker_fetch_error'))
    } finally {
      isSubmitting.value = false
    }
  }

  async function update(data) {
    if (!hasPermission('organization-workers', 'edit')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }

    isSubmitting.value = true

    try {
      const payload = {
        name: data.name?.value || '',
        email: data.email?.value || '',
        realEmail: data.realEmail?.value || '',
        supervisors: data.supervisors?.value?.map(e => oid(e)) || [],
        locations: data.locations?.value?.map(e => oid(e)) || [],
        roles: data.roles?.value?.map(e => oid(e)) || [],
        privileges: data.privileges?.value || '',
        isComplaintManager: data.isComplaintManager?.value || false,
        company: data.company?.value?.map(e => oid(e)) || []
      }

      // const defaultPassword = defaultPassword
      const action = { $set: payload }

      await updateItem({ collection, query, action })

      // Check if worker is an existing user and update it.
      // If not and role is consultant or supervisor, register as new user.
      // const userQuery = { 'custom_data.worker_id': workerObjectId }
      // const item = await getItem({ collection: 'user', query: userQuery })
      // const userPayload = {
      //   "custom_data.username": payload.name,
      //   "custom_data.role": payload.privileges,
      //   "custom_data.worker_id": workerObjectId,
      //   "custom_data.ability": [ { action: 'manage', subject: 'all' } ],
      // }
      // const userAction = { $set: userPayload }

      // if (item) {
      //   await updateItem({ collection: 'user', query: userQuery, action: { $set: { deleted: false } } })
      //   await updateItem({ collection: 'user', query: { id: item.id }, action: userAction })
      // } else {
      //   await useJwt.register({ email: payload.email.toLowerCase(), password: defaultPassword, name })
      //   const logUser = await useJwt.login({ email: payload.email.toLowerCase(), password: defaultPassword })
      //   await updateItem({ collection: 'user', query: { id: logUser.id }, action: userAction })
      //   await useJwt.realm.allUsers[logUser.id].logOut()
      // }

      // If email changed, update it in Cognito
      const emailChanged = originalEmail.value && payload.email && payload.email !== originalEmail.value
      if (emailChanged) {
        try {
          await updateCognitoEmail({ currentEmail: originalEmail.value, newEmail: payload.email })
        } catch (error) {
          console.error(error)
          showErrorMessage(i18n.t('message.cognito_user_error'))
        }
      }

      // Add audit log entry
      await addAuditEntry({
        collection,
        documentId: workerObjectId,
        originalData: originalData.value,
        updatedData: {
          name: payload.name,
          email: payload.email,
          realEmail: payload.realEmail,
          supervisors: payload.supervisors,
          locations: payload.locations,
          roles: payload.roles,
          privileges: payload.privileges,
          isComplaintManager: payload.isComplaintManager,
          company: payload.company,
        },
      })

      showSuccessMessage(i18n.t('message.worker_updated'))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.worker_update_error'))
    } finally {
      router.push({ name: 'organization-worker-list' })
      isSubmitting.value = false
    }
  }

  return {
    update,
    show,
    worker,
    isSubmitting,
  }
}
